import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger.js";

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function ResetPassword() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const query = useQuery();

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const labels = {
    en: {
      title: "Reset your password",
      subtitle: "Enter your new password below.",
      password: "New password",
      confirm: "Confirm password",
      submit: "Reset password",
      submitting: "Saving...",
      backToLogin: "Back to login",
      mismatch: "Passwords do not match.",
      invalidLink: "This reset link is invalid or missing.",
      success: "Your password has been reset successfully.",
    },
    ar: {
      title: "إعادة تعيين كلمة المرور",
      subtitle: "أدخل كلمة المرور الجديدة في الأسفل.",
      password: "كلمة المرور الجديدة",
      confirm: "تأكيد كلمة المرور",
      submit: "حفظ كلمة المرور",
      submitting: "جاري الحفظ...",
      backToLogin: "العودة لتسجيل الدخول",
      mismatch: "كلمتا المرور غير متطابقتين.",
      invalidLink: "رابط إعادة التعيين غير صالح.",
      success: "تمت إعادة تعيين كلمة المرور بنجاح.",
    },
  }[lang];

  useEffect(() => {
    const t = query.get("token");
    const e = query.get("email");

    if (t && e) {
      setToken(t);
      setEmail(e);
    } else {
      setError(labels.invalidLink);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!token || !email) {
      setError(labels.invalidLink);
      return;
    }

    if (password !== confirm) {
      setError(labels.mismatch);
      return;
    }

    setLoading(true);

    try {
      // ✅ Laravel API
      const res = await api.apiPostJson("reset-password", {
        email,
        token,
        password,
      });

      if (!res?.message) {
        throw new Error("Reset failed");
      }

      setSuccess(labels.success);

      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      logger.error(err);
      setError(err?.message || "Error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
      <h3>{labels.title}</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {token && (
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder={labels.password}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="form-control mb-3"
          />

          <input
            type="password"
            placeholder={labels.confirm}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="form-control mb-3"
          />

          <button className="btn btn-primary">
            {loading ? labels.submitting : labels.submit}
          </button>
        </form>
      )}

      <div className="mt-3">
        <Link to="/login">{labels.backToLogin}</Link>
      </div>
    </div>
  );
}